'use strict';

const Gate = require('./gate');

/**
 * this class references a circuit and points to a specific qubit in
 * the system or circuits qubit array.  it is a wrapper for generating
 * gate objects to append to a circuits "ops" array.
 */
class Qubit {
    constructor(circuit, index, control) {
        this.circuit = circuit;
        this.index = Math.abs(index);
        this.control = typeof control === 'number' ? control : null;
    }

    /**
     * return qubit's assocaited circuit only if qubit is usable as a target.
     * otherwise throw error.
     */
    _valid() {
        if (this.control !== null) {
            throw new Error('attempt to use control qubit as target');
        }
        if (!this.circuit) {
            throw new Error('attempt to use freed qubit');
        }
        return this.circuit;
    }

    _alloc(circuit) {
        this.circuit = circuit;
        return this;
    }

    /**
     * return qubit clone that can only be used in control arrays
     * and allow for specifying open control gates
     */
    asControl(open) {
        return new Qubit(
            this.circuit,
            this.index,
            open ? -this.index : this.index,
        );
    }

    /**
     * release qubit index to be re-used. does no entanglement validation.
     */
    free() {
        if (this.circuit === null) {
            throw new Error('attempt to double free qubit');
        }
        this.circuit._free(this);
    }

    isFree() {
        return this.circuit === null;
    }

    swap(qubit, controls) {
        return this._valid()._addGate(Gate.type.SWAP, [this, qubit], controls);
    }

    // fredkin gate
    cswap(qubit, controls) {
        return this.swap(qubit, controls);
    }

    h(controls) {
        return this.hadamard(controls);
    }

    hadamard(controls) {
        return this._valid()._addGate(Gate.type.H, [this], controls);
    }

    not(controls) {
        return this._valid()._addGate(Gate.type.NOT, [this], controls);
    }

    /**
     * pauli-x
     */
    x(controls, angle) {
        return this._valid()._addGate(Gate.type.X, [this], controls, angle);
    }

    /**
     * pauli-y
     */
    y(controls, angle) {
        return this._valid()._addGate(Gate.type.Y, [this], controls, angle);
    }

    /**
     * pauli-z
     */
    z(controls, angle) {
        return this._valid()._addGate(Gate.type.Z, [this], controls, angle);
    }

    /**
     * x with phase
     */
    rx(angle, controls) {
        return this._valid()._addGate(Gate.type.RX, [this], controls, angle);
    }

    /**
     * y with phase
     */
    ry(angle, controls) {
        return this._valid()._addGate(Gate.type.RY, [this], controls, angle);
    }

    /**
     * z with phase
     */
    rz(angle, controls) {
        return this._valid()._addGate(Gate.type.RZ, [this], controls, angle);
    }

    // synonym for r2
    s(controls) {
        return this._valid()._addGate(Gate.type.S, [this], controls);
    }

    // inverse r2
    si(controls) {
        return this._valid()._addGate(Gate.type.SI, [this], controls);
    }

    // synonym for r4
    t(controls) {
        return this._valid()._addGate(Gate.type.T, [this], controls);
    }

    // inverse r4
    ti(controls) {
        return this._valid()._addGate(Gate.type.TI, [this], controls);
    }

    // synoym for square root not
    v(controls) {
        return this._valid()._addGate(Gate.type.V, [this], controls);
    }

    // synoym for inverse square root not
    vi(controls) {
        return this._valid()._addGate(Gate.type.VI, [this], controls);
    }

    // square root not
    // @deprecated
    srn(controls) {
        return this.v(controls);
    }

    // inverse square root not
    // @deprecated
    srni(controls) {
        return this.vi(controls);
    }

    // PI/2 phase gate
    r2(controls) {
        return this._valid()._addGate(Gate.type.R2, [this], controls);
    }

    // PI/4 phase gate
    r4(controls) {
        return this._valid()._addGate(Gate.type.R4, [this], controls);
    }

    // PI/8 phase gate
    r8(controls) {
        return this._valid()._addGate(Gate.type.R8, [this], controls);
    }

    cnot(controls) {
        return this.not(controls);
    }

    cx(controls, n) {
        return this.x(controls, n);
    }

    cy(controls, n) {
        return this.y(controls, n);
    }

    cz(controls, n) {
        return this.z(controls, n);
    }

    xx(qubit, controls, angle) {
        if (!(qubit instanceof Qubit)) {
            throw new Error('other target qubit required');
        }
        return this._valid()._addGate(
            Gate.type.XX,
            [this, qubit],
            controls,
            angle,
        );
    }

    yy(qubit, controls, angle) {
        if (!(qubit instanceof Qubit)) {
            throw new Error('other target qubit required');
        }
        return this._valid()._addGate(
            Gate.type.YY,
            [this, qubit],
            controls,
            angle,
        );
    }

    zz(qubit, controls, angle) {
        if (!(qubit instanceof Qubit)) {
            throw new Error('other target qubit required');
        }
        return this._valid()._addGate(
            Gate.type.ZZ,
            [this, qubit],
            controls,
            angle,
        );
    }
}

module.exports = Qubit;
